<script setup lang="ts">
import ConfirmDlg from '@/components/confirm-dlg.vue'
import dayjs from 'dayjs'
import KantonRadio from '@/components/kanton-radio.vue'
import KpTextfield from '@/components/kp-textfield.vue'
import KpToolbar from '@/components/kp-toolbar.vue'
import { computed, onMounted, ref, watch } from 'vue'
import { registerFormRouteGuard } from '@/composables/routerGuard'
import { errToStr } from '@/helpers/tools'
import { useRoute, useRouter } from 'vue-router'
import type { Ref } from 'vue'
import { useAuthUser, useGlobals } from '@/helpers/globals'
import { useKantonByKuerzel } from '@/composables/kanton'
import { useToast } from '@/composables/toast'
import { useI18n } from 'vue-i18n'
import { News } from '@/models/News'
import { formRules } from '@/helpers/validators'
import Editor from '@/components/html-editor/Editor.vue'
import { createModel } from '@kp/js-model'
import KpFieldlabel from '@/components/kp-fieldlabel.vue'

const initialLoading = ref(false)
const confirmDelDlg = ref(false)
const news: Ref<News | null> = ref(null)
const valid = ref(false)
const dateMenuFrom = ref(false)
const dateMenuUntil = ref(false)
const loading = ref(false)

const rules = formRules

registerFormRouteGuard(isDirty)

const route = useRoute()
const toast = useToast()
const benutzer = useAuthUser()
const { t } = useI18n()
const router = useRouter()
const isNew = computed(() => {
    return route.params.id === 'new'
})

defineEmits<{
    (e: 'update:model-value', value: string | boolean): void
}>()

watch(route, () => {
    loadRecord(route.params.id as string)
})

onMounted(() => {
    loadRecord(route.params.id as string)
})

async function loadRecord(id: string) {
    try {
        initialLoading.value = true
        if (isNew.value) {
            news.value = createModel(News)
            news.value.anzeige_von = dayjs().format('YYYY-MM-DD')
            news.value.anzeige_bis = dayjs().add(7, 'days').format('YYYY-MM-DD')
            const globals = useGlobals()
            const kanton = await useKantonByKuerzel(globals.customer)
            if (kanton) {
                news.value.kanton = kanton.kuerzel ? [kanton.kuerzel] : null
            }
        } else {
            const idNum = Number.parseInt(id, 10)
            news.value = await News.loadById(idNum)
        }
    } catch (res) {
        toast.add({ text: errToStr(res), color: 'error' })
    } finally {
        initialLoading.value = false
    }
}
async function onSubmit() {
    try {
        if (news.value) {
            news.value.ersteller_id = benutzer?.id || null
            await news.value.save()
            closeForm()
        }
    } catch (res) {
        toast.add({ text: errToStr(res), color: 'error' })
    }
}

async function onDestroy() {
    confirmDelDlg.value = true
}
function closeForm() {
    router.go(-1)
}

async function confirmDestroy() {
    try {
        if (news.value) {
            await news.value.destroy()
            news.value = null
            closeForm()
        }
    } catch (e) {
        toast.add({ text: errToStr(e), color: 'error' })
    }
}

function isDirty() {
    return news.value ? news.value.isDirty() : false
}

function formatDate(value: string | null) {
    return value ? dayjs(value).format('DD.MM.YYYY') : ''
}

function showFromChanged(value: string | null) {
    dateMenuFrom.value = false
    if (news.value) {
        if (dayjs(news.value.anzeige_bis).isBefore(dayjs(value))) {
            news.value.anzeige_von = news.value.anzeige_bis
            news.value.anzeige_bis = value
        } else {
            news.value.anzeige_von = value
        }
    }
}
function showUntilChanged(value: string | null) {
    dateMenuUntil.value = false
    if (news.value) {
        if (news.value && dayjs(news.value.anzeige_von).isAfter(dayjs(value))) {
            news.value.anzeige_bis = news.value.anzeige_von
            news.value.anzeige_von = value
        } else {
            news.value.anzeige_bis = value
        }
    }
}
</script>

<template>
    <KpToolbar icon="fas fa-rss" :title="t('news')"> </KpToolbar>
    <v-skeleton-loader
        class="centered-card"
        v-if="initialLoading"
        type="list-item-avatar, list-item-three-line@3, actions"
    ></v-skeleton-loader>
    <div v-else>
        <v-container fluid v-if="news">
            <v-card class="centered-card">
                <v-card-title class="headline mt-2 ml-4">{{
                    news.titel || t('new_news_entry')
                }}</v-card-title>
                <v-card-text>
                    <v-form v-model="valid" autocomplete="new-password" class="ml-4 mb-4">
                        <KantonRadio v-model="news.kanton" multi style="margin-left: -0.5rem" />
                        <kp-textfield
                            v-model="news.titel"
                            :label="t('title')"
                            :rules="[rules.required]"
                        ></kp-textfield>
                        <Editor v-model:model-value="news.inhalt" class="mt-4" />
                        <v-checkbox
                            style="margin-left: -0.5rem"
                            v-model="news.statische_anzeige"
                            :label="t('static_display')"
                        >
                        </v-checkbox>
                        <v-row>
                            <v-col cols="12" xs="12" sm="8" md="4">
                                <KpFieldlabel
                                    :label="t('show_from')"
                                    :disabled="news.statische_anzeige"
                                />
                                <v-menu
                                    v-model="dateMenuFrom"
                                    :close-on-content-click="false"
                                    :disabled="news.statische_anzeige"
                                    transition="scale-transition"
                                    offset-y
                                    max-width="290px"
                                    min-width="290px"
                                >
                                    <template v-slot:activator="{ props }">
                                        <v-text-field
                                            class="inputfield"
                                            clearable
                                            :disabled="news.statische_anzeige"
                                            clear-icon="fas fa-times"
                                            :value="formatDate(news.anzeige_von)"
                                            required
                                            readonly
                                            v-bind="props"
                                        >
                                            <template v-slot:prepend-inner>
                                                <v-icon class="icon-margin" size="x-small"
                                                    >fas fa-calendar</v-icon
                                                >
                                            </template>
                                        </v-text-field>
                                    </template>
                                    <v-date-picker
                                        class="ml-4"
                                        :model-value="news.anzeige_von"
                                        first-day-of-week="1"
                                        @update:model-value="showFromChanged"
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" xs="12" sm="8" md="4">
                                <KpFieldlabel
                                    :label="t('show_until')"
                                    :disabled="news.statische_anzeige"
                                />
                                <v-menu
                                    v-model="dateMenuUntil"
                                    :close-on-content-click="false"
                                    :disabled="news.statische_anzeige"
                                    transition="scale-transition"
                                    offset-y
                                    max-width="290px"
                                    min-width="290px"
                                >
                                    <template v-slot:activator="{ props }">
                                        <v-text-field
                                            class="inputfield"
                                            clearable
                                            :disabled="news.statische_anzeige"
                                            clear-icon="fas fa-times"
                                            :value="formatDate(news.anzeige_bis)"
                                            required
                                            readonly
                                            v-bind="props"
                                        >
                                            <template v-slot:prepend-inner>
                                                <v-icon class="icon-margin" size="x-small"
                                                    >fas fa-calendar</v-icon
                                                >
                                            </template>
                                        </v-text-field>
                                    </template>
                                    <v-date-picker
                                        class="ml-4"
                                        :model-value="news.anzeige_bis"
                                        first-day-of-week="1"
                                        @update:model-value="showUntilChanged"
                                    ></v-date-picker>
                                </v-menu>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions class="ml-4">
                    <v-btn
                        v-if="!isNew"
                        class="hidden-sm-and-up"
                        color="red"
                        :disabled="loading"
                        text
                        @click="onDestroy"
                    >
                        <v-icon>fas fa-trash</v-icon>
                    </v-btn>
                    <v-btn
                        v-if="!isNew"
                        class="hidden-xs"
                        color="red"
                        :disabled="loading"
                        text
                        @click="onDestroy"
                    >
                        {{ t('delete') }}
                    </v-btn>

                    <v-spacer />
                    <v-btn
                        class="hidden-sm-and-up"
                        color="decline"
                        text
                        :disabled="loading"
                        @click="closeForm"
                        icon="fas fa-arrow-left"
                    >
                    </v-btn>
                    <v-btn
                        class="hidden-xs"
                        color="decline"
                        :disabled="loading"
                        text
                        @click="closeForm"
                    >
                        {{ t('close') }}
                    </v-btn>
                    <v-btn
                        class="hidden-xs"
                        variant="flat"
                        color="action white--text"
                        :disabled="!valid || loading"
                        :loading="loading"
                        @click="onSubmit"
                        prepend-icon="fa-solid fa-floppy-disk"
                    >
                        {{ t('save') }}
                    </v-btn>
                    <v-btn
                        class="hidden-sm-and-up"
                        color="action white--text"
                        :disabled="!valid || loading"
                        :loading="loading"
                        @click="onSubmit"
                        icon="fa-solid fa-floppy-disk"
                    >
                    </v-btn>
                </v-card-actions>
                <ConfirmDlg
                    v-model="confirmDelDlg"
                    :title="t('bestaetigung')"
                    :text="t('really_delete_rec')"
                    @yes="confirmDestroy"
                />
            </v-card>
        </v-container>
    </div>
</template>

<style lang="css" scoped>
.inputfield {
    margin-left: -1rem;
}
</style>
